import { useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { Play, Send, Rocket, ArrowLeft } from "lucide-react";

const EditeurMobile = ({
  exercice,
  code,
  setCode,
  languageId,
  runCode,
  handleSubmit,
  consoleStatus,
  consoleStatusColor,
  consoleMessage,
  testData,
  getLanguageExtension,
  fetchNextExercise,
  remainingTime,
  mode,
}) => {
  const [mobileStep, setMobileStep] = useState(1);

  const stepTitles = ["Énoncé", "Éditeur", "Résultat"];

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const stepStyle = (step) =>
    `flex-1 text-center py-2 text-sm font-semibold cursor-pointer transition-colors duration-200 ${
      mobileStep === step
        ? "bg-orange-500 text-white"
        : "bg-[#252e38] text-gray-300 hover:bg-[#3d4145]"
    }`;

  const handleStart = () => setMobileStep(2);

  const handleMobileSubmit = async () => {
    await handleSubmit();
    setMobileStep(1);
    if (typeof fetchNextExercise === "function") await fetchNextExercise();
  };

  return (
    <div className="min-h-screen bg-[#131c25] text-[#e0e0e0] flex flex-col relative">
      {/* Barre de navigation mobile FIXÉE */}
      <div className="flex border-b border-[#3d4145] sticky top-0 z-10 bg-[#131c25]">
        {stepTitles.map((title, index) => (
          <div
            key={index}
            className={stepStyle(index + 1)}
            onClick={() => setMobileStep(index + 1)}
          >
            {title}
          </div>
        ))}
      </div>

      {/* ✅ Timer en dessous de la barre de navigation mobile */}
      {mode === "evaluation" && (
        <div className="flex justify-end px-3 pt-2">
          <span
            className={`inline-block px-3 py-1 rounded text-sm font-mono shadow-md ${
              remainingTime <= 60
                ? "bg-red-700 text-white animate-pulse"
                : "bg-yellow-500 text-black"
            }`}
          >
            ⏱️ {formatTime(remainingTime)}
          </span>
        </div>
      )}
      {/* Contenu */}
      <div className="p-3 pt-2 flex-1">
        {mobileStep === 1 && (
          <div className="flex flex-col gap-4">
            {/* Énoncé */}
            <div className="bg-[#252e38] p-3 rounded-sm border border-[#3d4145] min-h-[350px]">
              <h2 className="text-sm font-semibold text-[#7a7c7e] border-b border-white/10 pb-2 mb-2">
                Énoncé
              </h2>
              <p className="text-orange-400 font-medium">{exercice?.intitule}</p>
            </div>

            {/* Bouton Commencer */}
            <button
              onClick={handleStart}
              className="bg-orange-600 hover:bg-orange-500 active:bg-orange-700 text-white py-2 rounded-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200"
            >
              <Rocket size={18} /> Commencer
            </button>
          </div>
        )}

        {mobileStep === 2 && (
          <div className="flex flex-col gap-4">
            {/* Éditeur */}
            <div className="bg-[#252e38] p-2 rounded-sm border border-[#3d4145]">
              <h2 className="text-sm font-semibold text-[#7a7c7e] border-b border-white/10 pb-2 mb-2">
                Éditeur de Code
              </h2>
              <div className="bg-[#111920] rounded-sm overflow-hidden text-white border border-[#3d4145] text-xs">
                <CodeMirror
                  value={code}
                  height="350px"
                  theme="dark"
                  extensions={[getLanguageExtension(languageId)]}
                  onChange={setCode}
                  basicSetup={{ lineNumbers: true }}
                  style={{ fontSize: "14px" }}
                />
              </div>
            </div>

            {/* Bouton Exécuter */}
            <button
              className="bg-green-600 hover:bg-green-500 active:bg-green-700 text-white py-2 rounded-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200"
              onClick={() => {
                runCode();
                setMobileStep(3);
              }}
            >
              <Play size={18} /> Exécuter
            </button>
          </div>
        )}

        {mobileStep === 3 && (
          <div className="flex flex-col gap-4">
            {/* Console */}
            <div className="bg-[#252e38] p-2 rounded-sm border border-[#3d4145]">
              <h2 className="text-sm font-semibold text-[#7a7c7e] border-b border-white/10 pb-2 mb-2">
                Console
              </h2>
              <div
                className="bg-[#1e1f26] p-3 rounded-sm font-mono whitespace-pre-wrap border border-[#3d4145] text-sm"
                style={{ minHeight: "100px" }}
              >
                {consoleStatus && (
                  <span style={{ color: consoleStatusColor, fontWeight: "bold" }}>
                    {consoleStatus}
                  </span>
                )}
                {consoleStatus && <br />}
                {consoleMessage}
              </div>
            </div>

            {/* Résultat des tests */}
            <div className="bg-[#252e38] p-2 rounded-sm border border-[#3d4145]">
              <h3 className="text-sm font-semibold text-[#7a7c7e] border-b border-white/10 pb-2 mb-2">
                Résultat des tests
              </h3>
              <p className="text-white">
                Test :{" "}
                <span style={{ color: testData.passed === testData.total ? "white" : "#ccc" }}>
                  {testData.total > 0 ? `${testData.passed}/${testData.total}` : "0/0"}
                </span>
              </p>
              <p className="text-white">
                % de Réussite :{" "}
                <span
                  style={{
                    color:
                      testData.percentage === 100
                        ? "limegreen"
                        : testData.percentage >= 50
                        ? "orange"
                        : "red",
                  }}
                >
                  {testData.percentage}%
                </span>
              </p>
            </div>

            {/* Bouton Retour */}
            <button
              onClick={() => setMobileStep(2)}
              className="bg-[#3d4145] hover:bg-blue-500 active:bg-blue-700 text-white py-2 rounded-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-150"
            >
              <ArrowLeft size={18} /> Retour à l’éditeur
            </button>

            {/* Bouton Soumettre */}
            <button
              onClick={handleMobileSubmit}
              className="bg-orange-600 hover:bg-orange-500 active:bg-orange-700 text-white py-2 rounded-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200"
            >
              <Send size={18} /> Soumettre
            </button>
          </div>
        )}
      </div>

      {/* ✅ TIMER FIXÉ EN BAS À DROITE
      {mode === "evaluation" && (
        <div className="fixed bottom-3 right-3 z-50">
          <span
            className={`inline-block px-3 py-1 rounded text-sm font-mono shadow-md ${
              remainingTime <= 60
                ? "bg-red-700 text-white animate-pulse"
                : "bg-yellow-500 text-black"
            }`}
          >
            ⏱️ {formatTime(remainingTime)}
          </span>
        </div>
      )} */}
    </div>
  );
};

export default EditeurMobile;
